import { GridColDef } from "@mui/x-data-grid";
import { Member, ofMember } from "../types/member";
import { Membership } from "../types/membership";
import React from "react";
import { Box, IconButton, Popover, Tooltip } from "@mui/material";
import { CheckCircle, Close } from "@mui/icons-material";
import BlockIcon from "@mui/icons-material/Block";
import { Coupon } from "../types/coupon";
import { MemberTicket } from "../types/ticketType";
import { Coin } from "../types/coin";
import { ImageColumn } from "../components/table/ImageColumn";
import { MoreActionsButton } from "../components/table/MoreActionsButton";
import IssueCoinMenuItem from "../components/table/IssueCoinMenuItem";
import { KycData } from "../components/KycData";
import { Branch, BranchAuthenticationType } from "../types/branch";

export const memberCols = (props: {
  branch: Branch;
  coin?: Coin | null;
  personalData?: boolean | null;
  t: (key: string) => string;
}): GridColDef<Member>[] => {
  const isKycBranch =
    props.branch.authenticationType === BranchAuthenticationType.KYC;
  // Name/Email/Company are never populated for KYC identities, so hide them
  // even though exposesPersonalData() is true for KYC (it's also used by
  // MemberForm and rsvpCols where the columns/fields are still meaningful).
  const showPersonalData = props.personalData && !isKycBranch;
  const cols: GridColDef<Member>[] = [
    ...(showPersonalData ? personalDataCols() : []),
    walletCol(),
    ...(props.coin ? [coinCol(props)] : []),
    ...(isKycBranch ? [kycCol()] : []),
    membershipsCol(props.t),
    ...(isKycBranch ? [] : [couponsCol()]),
    ...(isKycBranch ? [] : [ticketsCol()]),
    ...(props.coin ? [actionCol(props)] : []),
  ];
  if (isKycBranch) {
    // KYC branches drop Name/Email/Company/Coupons/Tickets, leaving only
    // three columns. Without explicit flex weights they huddle on the left
    // because the default column width is ~100px and membershipsCol's
    // flex:1 collapses to its (small) content width. Give wallet + passes
    // flex weights with a minWidth so the row spans the full table width.
    return cols.map((col) => {
      if (col.field === ofMember("walletAddress")) {
        return { ...col, flex: 1, minWidth: 160 };
      }
      if (col.field === ofMember("memberships")) {
        return { ...col, flex: 3, minWidth: 240 };
      }
      return col;
    });
  }
  return cols;
};

function personalDataCols(): GridColDef<Member>[] {
  return [
    {
      field: ofMember("name"),
      headerName: "Name",
      flex: 1,
      renderCell: ({ row }) => {
        if (!row.name) return "";
        return row.name;
      },
    },
    {
      field: ofMember("email"),
      headerName: "Email",
      flex: 1,
      renderCell: ({ row }) => {
        if (!row.email) return "";
        return row.email;
      },
    },
    {
      field: ofMember("company"),
      headerName: "Company",
      flex: 1,
      renderCell: ({ row }) => {
        if (!row.company) return "";
        return row.company;
      },
    },
  ];
}

function walletCol(): GridColDef<Member> {
  return {
    field: ofMember("walletAddress"),
    headerName: "Wallet Address",
    display: "flex",
    renderCell: (params) => {
      const address = params.value;
      var formattedAddress = address;
      if (!address) return <div></div>;
      if (address.length > 8) {
        formattedAddress = `${address.slice(0, 4)}...${address.slice(-4)}`;
      }
      return (
        <Tooltip title={address} arrow>
          <span style={{ cursor: "pointer", color: "#4832BB" }}>
            {formattedAddress}
          </span>
        </Tooltip>
      );
    },
  };
}

function coinCol(props: { coin?: Coin | null }): GridColDef<Member> {
  return {
    field: " ",
    renderCell: function render({ row }) {
      if (row.id == null) return <div></div>;
      return (
        <MoreActionsButton>
          <IssueCoinMenuItem
            resource={"issue-coin"}
            address={row.walletAddress}
            coinId={props.coin!.id as number}
          />
        </MoreActionsButton>
      );
    },
    align: "center",
    maxWidth: 80,
    filterable: false,
    sortable: false,
    disableExport: true,
    disableColumnMenu: true,
  };
}

function membershipsCol(t: (key: string) => string): GridColDef<Member> {
  return {
    field: ofMember("memberships"),
    headerName: t("pages.members.list.cols.memberships"),
    flex: 1,
    sortable: false,
    renderCell: (params) => {
      const maxDisplayCount = 5;
      return (
        <Box
          display="flex"
          flexDirection="row"
          gap={1}
          width="100%"
          height="100%"
        >
          {params.value
            .slice(0, maxDisplayCount)
            .map((membership: Membership) => (
              <MembershipElement membership={membership} />
            ))}
          {params.value.length > maxDisplayCount && (
            <span style={{ fontSize: "1rem", marginLeft: "0.5rem" }}>...</span>
          )}
        </Box>
      );
    },
  };
}

function ticketsCol(): GridColDef<Member> {
  return {
    field: ofMember("tickets"),
    headerName: "Tickets",
    flex: 1,
    renderCell: (params) => {
      const maxDisplayCount = 3;
      return (
        <Box
          display={"flex"}
          flexDirection={"row"}
          gap={1}
          width={"100%"}
          height={"100%"}
          alignItems={"center"}
        >
          {params.value
            .slice(0, maxDisplayCount)
            .map((ticket: MemberTicket) => (
              <>
                <Tooltip title={ticket.name} arrow>
                  <span style={{ cursor: "pointer" }}>
                    <ImageColumn
                      imageUrl={ticket.image?.url}
                      borderRadius={"10%"}
                      maxWidth={8}
                    />
                  </span>
                </Tooltip>
              </>
            ))}
          {params.value.length > maxDisplayCount && (
            <span style={{ fontSize: "1rem", marginLeft: "0.5rem" }}>...</span>
          )}
        </Box>
      );
    },
  };
}

function kycCol(): GridColDef<Member> {
  return {
    field: "",
    headerName: "KYC Data",
    sortable: false,
    align: "center",
    display: "flex",
    maxWidth: 100,
    renderCell: (params) => {
      const kycData = (params.row.additionalData as any)?.kyc;
      return <KycCell kycData={kycData} />;
    },
  };
}

function KycCell({ kycData }: { kycData: any }) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [pinned, setPinned] = React.useState(false);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (kycData && !pinned) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMouseLeave = () => {
    if (!pinned) {
      setAnchorEl(null);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (kycData) {
      setAnchorEl(event.currentTarget);
      setPinned(true);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setPinned(false);
  };

  const open = Boolean(anchorEl);

  if (!kycData) {
    return (
      <Tooltip title="KYC data not available" arrow>
        <BlockIcon sx={{ color: "text.secondary", fontSize: 28 }} />
      </Tooltip>
    );
  }
  return (
    <>
      <IconButton
        size="small"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        sx={{ padding: 0 }}
      >
        <CheckCircle sx={{ color: "success.main", fontSize: 28 }} />
      </IconButton>
      <Popover
        sx={pinned ? undefined : { pointerEvents: "none" }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={handleClose}
        disableRestoreFocus
      >
        <Box sx={{ p: 1 }}>
          {pinned && (
            <Box display="flex" justifyContent="flex-end" sx={{ mb: 0.5 }}>
              <IconButton size="small" onClick={handleClose}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          )}
          <KycData kycData={kycData} />
        </Box>
      </Popover>
    </>
  );
}

function couponsCol(): GridColDef<Member> {
  return {
    field: ofMember("coupons"),
    headerName: "Coupons",
    flex: 1,
    renderCell: (params) => {
      const maxDisplayCount = 3;
      return (
        <Box
          display={"flex"}
          flexWrap={"wrap"}
          overflow={"hidden"}
          flexDirection={"row"}
          gap={1}
          width={"100%"}
          height={"100%"}
          alignItems={"center"}
        >
          {params.value.slice(0, maxDisplayCount).map((coupon: Coupon) => (
            <>
              <Tooltip title={coupon.name} arrow>
                <span style={{ cursor: "pointer" }}>
                  <ImageColumn
                    imageUrl={coupon.image?.url}
                    borderRadius={"10%"}
                    maxWidth={8}
                  />
                </span>
              </Tooltip>
            </>
          ))}
          {params.value.length > maxDisplayCount && (
            <span style={{ fontSize: "1rem", marginLeft: "0.5rem" }}>...</span>
          )}
        </Box>
      );
    },
  };
}

function actionCol(props: { coin?: Coin | null }): GridColDef<Member> {
  return {
    field: " ",
    renderCell: function render({ row }) {
      if (row.id == null) return <div></div>;
      return (
        <MoreActionsButton>
          <IssueCoinMenuItem
            resource={"issue-coin"}
            address={row.walletAddress}
            coinId={props.coin!.id as number}
          />
        </MoreActionsButton>
      );
    },
    align: "center",
    maxWidth: 80,
    filterable: false,
    sortable: false,
    disableExport: true,
    disableColumnMenu: true,
  };
}

function MembershipElement(props: { membership: Membership }) {
  const membership = props.membership;
  return (
    <Tooltip title={membership.name} arrow>
      <span style={{ cursor: "pointer" }}>
        <ImageColumn
          imageUrl={membership.image?.url ?? membership.imageUrl ?? ""}
          borderRadius={"10%"}
        />
      </span>
    </Tooltip>
  );
}
